
const CHARACTERS_THAT_DONT_NEED_COMMA = [",", "(", "+", "-", "*", "/", "=", " "];
const CHARACTERS_THAT_DONT_NEED_COMMA_AFTER = [",", ")", "+", "-", "*", "/", "=", " "];

/**
 * Finds the position of the last unclosed opening parenthesis
 *
 * @param {string} formulaText - The current formula text
 * @param {number} currentPosition - Position to search backwards from
 * @returns {number} - Index of last unclosed "(", or -1 if not found
 */
const findLastUnclosedOpeningParenthesis = (formulaText, currentPosition) => {
  let unclosedParenthesesCount = 0;

  for (let index = currentPosition - 1; index >= 0; index--) {
    const character = formulaText[index];

    if (character === ")") {
      unclosedParenthesesCount++;
    } else if (character === "(") {
      if (unclosedParenthesesCount === 0) {
        return index;
      }
      unclosedParenthesesCount--;
    }
  }

  return -1;
};

/**
 * Determines if a comma should be automatically added before inserting new content
 *
 * Example scenarios:
 * - "=SUM(Column1" + inserting "Column2" → needs comma → "=SUM(Column1, Column2"
 * - "=SUM(" + inserting "Column1" → no comma needed → "=SUM(Column1"
 * - "=SUM(Column1," + inserting "Column2" → no comma needed → "=SUM(Column1, Column2"
 *
 * @param {string} formulaText - Current formula text
 * @param {number} insertPosition - Position where new text will be inserted
 * @returns {boolean} - True if comma should be prepended
 */
export const shouldAutoInsertComma = (formulaText, insertPosition) => {
  if (!formulaText || insertPosition <= 0) {
    return false;
  }

  // Find if we're inside a function (has unclosed parenthesis)
  const lastOpenParenthesisIndex = findLastUnclosedOpeningParenthesis(formulaText, insertPosition);

  if (lastOpenParenthesisIndex === -1) {
    // Not inside any function call
    return false;
  }

  // Get the content between the opening parenthesis and cursor
  const contentInsideFunction = formulaText.substring(lastOpenParenthesisIndex + 1, insertPosition);
  const trimmedContent = contentInsideFunction.trim();

  // If there's no content yet (first argument), no comma needed
  if (trimmedContent.length === 0) {
    return false;
  }

  // Find the last non-whitespace character before cursor
  let lastNonSpaceIndex = insertPosition - 1;
  while (lastNonSpaceIndex >= 0 && formulaText[lastNonSpaceIndex] === " ") {
    lastNonSpaceIndex--;
  }

  if (lastNonSpaceIndex < 0) {
    return false;
  }

  const lastCharacterBeforeCursor = formulaText[lastNonSpaceIndex];

  // If the last character is already a separator/operator, no comma needed
  if (CHARACTERS_THAT_DONT_NEED_COMMA.includes(lastCharacterBeforeCursor)) {
    return false;
  }

  // Otherwise, we have content and need a comma before the new argument
  return true;
};

/**
 * Prepares text for insertion with automatic comma if needed
 *
 * @param {string} formulaText - Current formula text
 * @param {number} insertPosition - Position where text will be inserted
 * @param {string} textToInsert - The text to insert (column name or function)
 * @returns {string} - Text to insert (with comma prefix if needed)
 */
export const prepareTextForInsertion = (formulaText, insertPosition, textToInsert) => {
  if (!formulaText || typeof formulaText !== "string") {
    return textToInsert;
  }
  const needsLeadingComma = shouldAutoInsertComma(formulaText, insertPosition);
  let needsTrailingComma = false;
  const lastOpenParenthesisIndex = findLastUnclosedOpeningParenthesis(formulaText, insertPosition);
  if (lastOpenParenthesisIndex !== -1) {
    // Find next non-space character after insertion point
    let nextNonSpaceIndex = insertPosition;
    while (nextNonSpaceIndex < formulaText.length && formulaText[nextNonSpaceIndex] === " ") {
      nextNonSpaceIndex++;
    }

    if (nextNonSpaceIndex < formulaText.length) {
      const nextChar = formulaText[nextNonSpaceIndex];
      // If we're before another token (not a comma/close-paren/operator), we should add a trailing comma.
      if (!CHARACTERS_THAT_DONT_NEED_COMMA_AFTER.includes(nextChar)) {
        needsTrailingComma = true;
      }
    }
  }

  const prefix = needsLeadingComma ? ", " : "";
  const suffix = needsTrailingComma ? ", " : "";
  return `${prefix}${textToInsert}${suffix}`;
};

// ============================================================================
// PARENTHESES VALIDATION
// ============================================================================

/**
 * Validates that all parentheses in the formula are properly balanced
 *
 * @param {string} formulaText - The formula to validate
 * @returns {{isValid: boolean, errorMessage: string|null}}
 */
export const validateBalancedParentheses = (formulaText) => {
  if (!formulaText) {
    return { isValid: true, errorMessage: null };
  }

  let openParenthesesCount = 0;
  let closeParenthesesCount = 0;

  for (const character of formulaText) {
    if (character === "(") {
      openParenthesesCount++;
    } else if (character === ")") {
      closeParenthesesCount++;

      // Found a closing parenthesis before an opening one
      if (closeParenthesesCount > openParenthesesCount) {
        return {
          isValid: false,
          errorMessage: "Unmatched closing parenthesis"
        };
      }
    }
  }

  if (openParenthesesCount > closeParenthesesCount) {
    const missingCount = openParenthesesCount - closeParenthesesCount;
    return {
      isValid: false,
      errorMessage: `Missing ${missingCount} closing parenthesis${missingCount > 1 ? "es" : ""}`
    };
  }

  return { isValid: true, errorMessage: null };
};

/**
 * Performs basic frontend validations on a formula
 *
 * NOTE: This only checks basic syntax. All business logic validations
 * (argument counts, data types, custom functions) should be done by backend.
 *
 * @param {string} formula - The formula to validate
 * @returns {{isValid: boolean, errorMessage: string|null}}
 */
export const validateBasicFormulaSyntax = (formula) => {
  // Check for empty formula
  if (!formula || typeof formula !== "string") {
    return { isValid: false, errorMessage: "Formula is required" };
  }

  const trimmedFormula = formula.trim();

  // Check if formula starts with "="
  if (!trimmedFormula.startsWith("=")) {
    return { isValid: false, errorMessage: 'Formula must start with "="' };
  }

  // Get formula body (everything after "=")
  const formulaBody = trimmedFormula.substring(1).trim();

  // Check for empty formula body
  if (formulaBody.length === 0) {
    return { isValid: false, errorMessage: "Formula cannot be empty after =" };
  }

  // Validate parentheses are balanced
  const parenthesesResult = validateBalancedParentheses(formulaBody);
  if (!parenthesesResult.isValid) {
    return parenthesesResult;
  }

  // Check for empty function calls like "SUM()" or "AVERAGE()"
  const emptyFunctionPattern = /[A-Z]+\s*\(\s*\)/i;
  if (emptyFunctionPattern.test(formulaBody)) {
    const match = formulaBody.match(/([A-Z]+)\s*\(\s*\)/i);
    const functionName = match ? match[1].toUpperCase() : "Function";
    return {
      isValid: false,
      errorMessage: `${functionName}() cannot be empty - add at least one argument`
    };
  }

  // Check for trailing commas before closing parenthesis like "SUM(a, b,)"
  const trailingCommaPattern = /,\s*\)/;
  if (trailingCommaPattern.test(formulaBody)) {
    return {
      isValid: false,
      errorMessage: "Remove trailing comma before closing parenthesis"
    };
  }

  // Check for multiple consecutive commas like "SUM(a,,b)"
  const consecutiveCommasPattern = /,\s*,/;
  if (consecutiveCommasPattern.test(formulaBody)) {
    return {
      isValid: false,
      errorMessage: "Remove extra commas - found multiple consecutive commas"
    };
  }

  // Check for comma immediately after opening parenthesis like "SUM(,a)"
  const leadingCommaPattern = /\(\s*,/;
  if (leadingCommaPattern.test(formulaBody)) {
    return {
      isValid: false,
      errorMessage: "Remove leading comma after opening parenthesis"
    };
  }

  // All basic validations passed
  // Backend will handle: argument counts, data types, custom functions
  return { isValid: true, errorMessage: null };
};
